import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FormikValues } from 'formik'
import { differenceInMinutes } from 'date-fns'
import { Helmet } from 'react-helmet-async'

import ConfirmSignUp from './ConfirmSignUp'

import { ROUTE_HOME, ROUTE_LOGIN } from '~/routes/Routes'
import { RootState, useAppDispatch } from '~/redux/store'
import { confirmSignUp, login, resendSignUpCode } from '~/redux/features/auth/actions'
import { addNotification } from '~/redux/features/notifications/notificationSlice'

import { AWSException } from '~/types/errors/AWSException'

const ConfirmSignUpView: React.FC = (): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const user = useSelector((state: RootState) => state.auth.user)
  const signUp = useSelector((state: RootState) => state.auth.signUp)

  const handleConfirm = async (values: FormikValues): Promise<void> => {
    const username = signUp.email ?? user?.email

    setLoading(true)

    try {
      await dispatch(confirmSignUp({
        username,
        code: values.code,
      })).unwrap()
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: 'There was a problem confirming your account',
      }))

      setLoading(false)

      return
    }

    if (!user) {
      try {
        await dispatch(login({
          email: username,
          password: signUp.password,
        })).unwrap()
      } catch (error) {
        return navigate(ROUTE_LOGIN)
      }
    }

    setLoading(false)

    navigate(ROUTE_HOME)
  }

  const resendCode = async (): Promise<void> => {
    const lastRequest = localStorage.getItem('resendSignUpCode')
    const timeElapsed = differenceInMinutes(new Date(), lastRequest ? new Date(Number(lastRequest)) : new Date())

    if (lastRequest && timeElapsed < 5) {
      dispatch(addNotification({
        type: 'error',
        message: 'Limit reached, wait 5 minutes before requesting a new code',
      }))
      return
    }

    localStorage.setItem('resendSignUpCode', Date.now().toString())

    try {
      await dispatch(resendSignUpCode(signUp.email)).unwrap()
    } catch (error) {
      if ((error as AWSException).name === 'LimitExceededException') {
        dispatch(addNotification({
          type: 'error',
          message: 'Limit reached, wait 30 minutes before requesting a new code',
        }))
      } else {
        dispatch(addNotification({
          type: 'error',
          message: 'There was a problem resending the code',
        }))
      }
    }
  }

  return <>
    <Helmet>
      <title>Confirm Sign Up | Ecover Loyalty Rewards</title>
    </Helmet>
    <ConfirmSignUp email={signUp.email} isLoading={loading} onResendCode={resendCode} onConfirm={handleConfirm} />
  </>
}

export default ConfirmSignUpView
